"use client";

import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";
import { getPlatformColor, getStatusColor } from "./platform-colors";
import { EventStatusBadge } from "./event-status-badge";
import { PublishFailureDetails } from "./publish-failure-details";
import type { CalendarEvent } from "@content-os/types";
import { POST_TYPE_META, type PostType } from "@/hooks/use-post-generation";
import { PlatformIcon } from "@/components/ui/platform-icon";
import { MediaPreview, mediaItemFromPiece } from "@/components/media";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { TFunction } from "i18next";

/** Drag MIME for rescheduling an existing scheduled post by its id. */
export const RESCHEDULE_DND_TYPE = "application/x-reschedule";

/** Classes toggled on a calendar slot while a droppable item hovers it.
 *  `cal-drop-active` is a styleless marker so an aborted drag can find and
 *  clear the highlight. Shared by the slot drop targets and this card's
 *  drag-abort cleanup so they can never drift apart. */
export const DROP_RING = ["ring-2", "ring-primary", "ring-inset", "cal-drop-active"];

/** Marks a click/drag as landing on an event card (vs. empty slot background),
 *  so slot cells can ignore it for click-to-create. */
export const EVENT_CARD_MARKER = "data-calendar-event";

/** True when a dragover carries a payload a calendar slot accepts. */
export function isCalendarDragType(types: readonly string[]): boolean {
  return types.includes(RESCHEDULE_DND_TYPE) || types.includes("application/json");
}

/** Build a MediaItem from an event's media, or null when there's nothing. */
function eventMediaItem(event: CalendarEvent) {
  return mediaItemFromPiece({
    id: event.id,
    mediaUrls: event.mediaUrls,
    thumbnailUrl: event.thumbnailUrl,
    format: (event.piece?.format ?? event.postType) as string | null,
    title: event.title,
  });
}

interface EventCardProps {
  event: CalendarEvent;
  onClick: (event: CalendarEvent) => void;
  compact?: boolean;
  /** Brand timezone for rendering event times; defaults to browser-local. */
  tz?: string;
}

function platformLabel(p: string, t: TFunction): string {
  return t(`platform.${p}`, { defaultValue: p.charAt(0) + p.slice(1).toLowerCase() });
}

function pieceLabel(event: CalendarEvent, t: TFunction): string {
  const pt = (event.piece?.postType ?? event.postType) as PostType | null | undefined;
  if (pt && POST_TYPE_META[pt]) {
    return `${platformLabel(POST_TYPE_META[pt].platform, t)} ${POST_TYPE_META[pt].label}`;
  }
  return platformLabel(event.platform, t);
}

function EventTooltip({ event, tz }: { event: CalendarEvent; tz?: string }) {
  const { t } = useTranslation("calendar");
  const locale = useLocale();
  const caption = event.caption ?? event.piece?.caption ?? "";
  const scheduled = new Date(event.scheduledFor);
  const media = eventMediaItem(event);
  return (
    <div className="max-w-xs space-y-2">
      <div className="flex items-center gap-1.5">
        <PlatformIcon platform={event.platform} size={14} />
        <span className="font-medium text-xs">{pieceLabel(event, t)}</span>
      </div>
      {media && <MediaPreview item={media} autoPlay className="aspect-video w-full" />}
      <div className="text-[11px] text-muted-foreground">
        {formatDate(scheduled, locale, {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          timeZone: tz,
        })}
      </div>
      {caption && (
        <div className="text-xs line-clamp-4 whitespace-pre-wrap">{caption}</div>
      )}
      {event.projectTitle && (
        <div className="text-[11px] text-muted-foreground">
          {t("eventCard.project", { title: event.projectTitle })}
        </div>
      )}
      {event.creatorName && (
        <div className="text-[11px] text-muted-foreground">
          {t("eventCard.by", { name: event.creatorName })}
        </div>
      )}
      <div className="text-[11px] flex items-center gap-1">
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: getStatusColor(event.status) }}
        />
        {t(`status.${event.status}`, { defaultValue: event.status.charAt(0) + event.status.slice(1).toLowerCase() })}
      </div>
      {event.status === "FAILED" && <PublishFailureDetails failure={event} compact />}
    </div>
  );
}

export function EventCard({ event, onClick, compact, tz }: EventCardProps) {
  const { t } = useTranslation("calendar");
  const locale = useLocale();
  const platformColor = getPlatformColor(event.platform);
  const statusColor = getStatusColor(event.status);
  const time = formatDate(new Date(event.scheduledFor), locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: tz,
  });
  const label = pieceLabel(event, t);
  const media = eventMediaItem(event);

  // Only scheduled posts can be dragged to a new slot to reschedule.
  const draggable = event.status === "SCHEDULED";
  const dragProps = draggable
    ? {
        draggable: true,
        onDragStart: (e: React.DragEvent) => {
          e.dataTransfer.setData(RESCHEDULE_DND_TYPE, event.id);
          e.dataTransfer.effectAllowed = "move";
          // Clear any cell highlight left behind if the drag is aborted
          // (Esc / dropped outside a slot, where no slot dragleave/drop fires).
          const clear = () => {
            document
              .querySelectorAll(".cal-drop-active")
              .forEach((el) => el.classList.remove(...DROP_RING));
            document.removeEventListener("dragend", clear);
          };
          document.addEventListener("dragend", clear);
        },
      }
    : {};
  const dragCursor = draggable ? " cursor-grab active:cursor-grabbing" : "";

  const trigger = compact ? (
    <button
      {...dragProps}
      {...{ [EVENT_CARD_MARKER]: "" }}
      onClick={() => onClick(event)}
      className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-xs hover:opacity-80 transition-opacity w-full text-left${dragCursor}`}
      style={{ backgroundColor: `${platformColor}20`, borderLeft: `3px solid ${statusColor}` }}
    >
      <PlatformIcon platform={event.platform} size={11} className="flex-shrink-0" />
      <span className="truncate">{time}</span>
      <span className="text-[10px] text-muted-foreground truncate">{label}</span>
      <span className="ml-auto flex-shrink-0">
        <EventStatusBadge status={event.status} compact />
      </span>
    </button>
  ) : (
    <button
      {...dragProps}
      {...{ [EVENT_CARD_MARKER]: "" }}
      onClick={() => onClick(event)}
      className={`flex flex-col gap-1 rounded-lg p-2 hover:opacity-80 transition-opacity w-full text-left border${dragCursor}`}
      style={{ borderColor: platformColor, backgroundColor: `${platformColor}08` }}
    >
      <div className="flex items-center gap-1.5 text-xs">
        <PlatformIcon platform={event.platform} size={13} className="flex-shrink-0" />
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground ml-auto">{time}</span>
      </div>

      {media && <MediaPreview item={media} hoverPlay={false} className="aspect-video w-full" />}

      <div className="flex items-center justify-between text-xs">
        <span className="font-mono text-muted-foreground">{event.title}</span>
        <EventStatusBadge status={event.status} />
      </div>
    </button>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side="top" className="p-3">
          <EventTooltip event={event} tz={tz} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
